/**
 * rightfont-service — reads a RightFont `.rightfontlibrary` folder's JSON
 * metadata to provide family-grouped font listings with collections and
 * categories.
 *
 * A `.rightfontlibrary` folder contains:
 *   fonts/           — actual font files, organized {Letter}/{Family}/
 *   metadata/fonts/  — per-font JSON metadata (.rightfontmetadata)
 *   metadata/fontlists/ — collection JSON metadata
 *   manifest.rightfontmetadata — library manifest
 *
 * This reads the JSON files directly (no SQLite needed), so it works on any
 * machine where the library folder is synced (e.g. via Dropbox).
 */

import { existsSync, readdirSync, readFileSync } from "node:fs";
import { join } from "node:path";

// Category IDs from RightFont's schema
const CATEGORY_LABELS: Record<number, string> = {
  1: "Sans Serif",
  2: "Monospace",
  4: "Display",
  5: "Handwriting",
  6: "Serif",
};

export interface RightFontEntry {
  id: string;
  name: string;
  postscriptName: string;
  family: string;
  familyGroup: string;
  style: string;
  weight: number;
  weightValue: number;
  category: string;
  format: string;
  /** Absolute path to the font file on disk. */
  absolutePath: string;
  /** Relative location within the library (e.g. "F/Fabrikat/file.otf"). */
  location: string;
  starred: boolean;
}

export interface RightFontFamily {
  familyGroup: string;
  category: string;
  fonts: RightFontEntry[];
}

export interface RightFontCollection {
  id: string;
  name: string;
  fontCount: number;
  fontIds: string[];
}

export interface RightFontLibraryData {
  families: RightFontFamily[];
  collections: RightFontCollection[];
  totalFonts: number;
}

/**
 * Check if a path looks like a .rightfontlibrary folder.
 */
export function isRightFontLibrary(libraryPath: string): boolean {
  return (
    libraryPath.endsWith(".rightfontlibrary") &&
    existsSync(join(libraryPath, "fonts")) &&
    existsSync(join(libraryPath, "metadata"))
  );
}

/**
 * Read a .rightfontlibrary folder — fonts grouped by family, plus collections.
 */
export function readRightFontLibrary(libraryPath: string): RightFontLibraryData {
  if (!isRightFontLibrary(libraryPath)) {
    return { families: [], collections: [], totalFonts: 0 };
  }

  const fontsDir = join(libraryPath, "fonts");
  const metadataFontsDir = join(libraryPath, "metadata", "fonts");
  const metadataListsDir = join(libraryPath, "metadata", "fontlists");

  // Read all font metadata JSON files
  const familyMap = new Map<string, RightFontFamily>();
  let totalFonts = 0;

  if (existsSync(metadataFontsDir)) {
    const files = readdirSync(metadataFontsDir).filter((f) => f.endsWith(".rightfontmetadata"));

    for (const file of files) {
      try {
        const raw = readFileSync(join(metadataFontsDir, file), "utf-8");
        const meta = JSON.parse(raw) as Record<string, any>;
        if (meta.kind !== "RFFont" || !meta.familyGroup) continue;

        const cat = CATEGORY_LABELS[meta.category as number] ?? "Other";
        const location = meta.location as string;
        const absolutePath = join(fontsDir, location);

        const entry: RightFontEntry = {
          id: meta.uuid as string,
          name: meta.name as string,
          postscriptName: (meta.postscriptName as string) ?? "",
          family: (meta.family as string) ?? "",
          familyGroup: meta.familyGroup as string,
          style: (meta.style as string) ?? "Regular",
          weight: (meta.weight as number) ?? 2,
          weightValue: (meta.weightValue as number) ?? 0,
          category: cat,
          format: meta.format === 1 ? "OTF" : "TTF",
          absolutePath,
          location,
          starred: meta.starred === true,
        };

        let fam = familyMap.get(entry.familyGroup);
        if (!fam) {
          fam = { familyGroup: entry.familyGroup, category: cat, fonts: [] };
          familyMap.set(entry.familyGroup, fam);
        }
        fam.fonts.push(entry);
        totalFonts++;
      } catch {
        // Skip unparseable metadata files
      }
    }
  }

  // Sort fonts within each family by weight then style
  for (const fam of familyMap.values()) {
    fam.fonts.sort((a, b) => a.weight - b.weight || a.style.localeCompare(b.style));
  }

  // Read collection metadata
  const collections: RightFontCollection[] = [];
  if (existsSync(metadataListsDir)) {
    const files = readdirSync(metadataListsDir).filter((f) => f.endsWith(".rightfontmetadata"));
    for (const file of files) {
      try {
        const raw = readFileSync(join(metadataListsDir, file), "utf-8");
        const meta = JSON.parse(raw) as Record<string, any>;
        if (meta.kind !== "RFCollection") continue;
        const fontIds = Array.isArray(meta.fonts) ? (meta.fonts as string[]) : [];
        collections.push({
          id: meta.uuid as string,
          name: meta.name as string,
          fontCount: fontIds.length,
          fontIds,
        });
      } catch {
        // Skip unparseable
      }
    }
    collections.sort((a, b) => a.name.localeCompare(b.name));
  }

  const families = Array.from(familyMap.values()).sort((a, b) =>
    a.familyGroup.localeCompare(b.familyGroup),
  );

  return { families, collections, totalFonts };
}
